import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import useCampaignForecastConfidence from '../../../hooks/stats/useCampaignForecastConfidence';
import {
    I18N,
    formatConfidence,
    formatCount,
    formatHourLabel,
    formatRequestInterval,
    getHourStatusStyle,
    getRegularityLabel,
} from '../../../utils/forecastConfidence';

const COLORS = {
    delivered: '#3B82F6',
    missed: '#DC2626',
    missedShortfall: 'rgba(220, 38, 38, 0.35)',
    forecast: '#1F2937',
    requests: '#D97706',
};

const isShortfall = (hour) =>
    Number(hour.delivered_impressions || 0) <
    Number(hour.forecast_impressions || 0);

const buildChart = (hours, withDay, t) => {
    const labels = hours.map((hour) => formatHourLabel(hour.hour_utc, withDay));
    const data = {
        labels,
        datasets: [
            {
                type: 'line',
                label: t(`${I18N}.detail.chart-forecast`),
                data: hours.map((hour) => hour.forecast_impressions),
                yAxisID: 'impressions',
                borderColor: COLORS.forecast,
                backgroundColor: COLORS.forecast,
                borderWidth: 2,
                pointRadius: 2,
                fill: false,
                lineTension: 0,
            },
            {
                type: 'line',
                label: t(`${I18N}.detail.chart-requests`),
                // null stays null so chart.js leaves a gap instead of a 0.
                data: hours.map((hour) => hour.requests),
                yAxisID: 'requests',
                borderColor: COLORS.requests,
                backgroundColor: COLORS.requests,
                borderDash: [6, 4],
                borderWidth: 2,
                pointRadius: 0,
                fill: false,
                lineTension: 0,
                spanGaps: false,
            },
            {
                type: 'bar',
                label: t(`${I18N}.detail.chart-delivered`),
                data: hours.map((hour) => hour.delivered_impressions),
                yAxisID: 'impressions',
                backgroundColor: hours.map((hour) =>
                    hour.status === 'missed_opportunity'
                        ? COLORS.missed
                        : COLORS.delivered
                ),
            },
            {
                // The shortfall of missed-opportunity hours, so they stand out
                // even when nothing was delivered.
                type: 'bar',
                key: 'missed',
                label: t(`${I18N}.detail.chart-missed`),
                data: hours.map((hour) =>
                    hour.status === 'missed_opportunity'
                        ? Number(hour.forecast_impressions || 0) -
                          Number(hour.delivered_impressions || 0)
                        : null
                ),
                yAxisID: 'impressions',
                backgroundColor: COLORS.missedShortfall,
                borderColor: COLORS.missed,
                borderWidth: 1,
            },
        ],
    };
    const options = {
        maintainAspectRatio: false,
        legend: { position: 'bottom', labels: { boxWidth: 12 } },
        tooltips: {
            mode: 'index',
            intersect: false,
            // Only missed-opportunity hours have a shortfall bar; elsewhere
            // its null value would read "NaN".
            filter: (item, chartData) => {
                const dataset = chartData.datasets[item.datasetIndex];
                return !(
                    dataset.key === 'missed' &&
                    dataset.data[item.index] === null
                );
            },
            // Unknown requests read "—", like the table.
            callbacks: {
                label: (item, chartData) => {
                    const dataset = chartData.datasets[item.datasetIndex];
                    return `${dataset.label}: ${formatCount(
                        dataset.data[item.index]
                    )}`;
                },
            },
        },
        scales: {
            xAxes: [{ gridLines: { display: false } }],
            yAxes: [
                {
                    id: 'impressions',
                    position: 'left',
                    ticks: { beginAtZero: true, precision: 0 },
                    scaleLabel: {
                        display: true,
                        labelString: t(`${I18N}.detail.axis-impressions`),
                    },
                },
                {
                    id: 'requests',
                    position: 'right',
                    ticks: { beginAtZero: true, precision: 0 },
                    gridLines: { drawOnChartArea: false },
                    scaleLabel: {
                        display: true,
                        labelString: t(`${I18N}.detail.axis-requests`),
                    },
                },
            ],
        },
    };
    return { data, options };
};

/**
 * Hour-by-hour forecast, delivery and requests of one screen of the campaign,
 * for the dashboard period. Missed-opportunity hours are red in the chart and
 * flagged in the table.
 */
function ForecastConfidenceScreenDetail({
    campaignId,
    range,
    screen,
    requestConfig,
    onClose,
}) {
    const { t } = useTranslation();
    const [onlyShortfall, setOnlyShortfall] = useState(false);
    const { data, error, isLoading } = useCampaignForecastConfidence({
        campaignId,
        from: range?.from,
        to: range?.to,
        screenId: screen?.screen_id,
        requestConfig,
    });

    const hours = data?.hours || [];
    const withDay =
        !!range && moment(range.to).diff(moment(range.from), 'hours') > 24;
    const chart = useMemo(
        () => buildChart(hours, withDay, t),
        [hours, withDay, t]
    );
    const visibleHours = onlyShortfall ? hours.filter(isShortfall) : hours;
    const detailScreen = data?.screen || screen;
    const screenName = detailScreen?.screen_name || detailScreen?.screen_id;

    return (
        <section
            className="mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
            data-testid="forecast-confidence-detail"
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h3 className="font-light text-sm uppercase">
                        {t(`${I18N}.detail.title`, { screen: screenName })}
                    </h3>
                    {detailScreen && (
                        <p className="text-xs text-gray-600 mt-1">
                            {t(`${I18N}.columns.confidence`)}:{' '}
                            <b>{formatConfidence(detailScreen.confidence)}</b>
                            {' · '}
                            {t(`${I18N}.columns.regularity`)}:{' '}
                            {getRegularityLabel(detailScreen.regularity, t)}
                            {' · '}
                            {t(`${I18N}.columns.interval`)}:{' '}
                            {formatRequestInterval(
                                detailScreen.avg_request_interval_sec,
                                t
                            )}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-xs border border-gray-300 bg-white rounded px-2 py-1 hover:bg-gray-100"
                >
                    ✕ {t(`${I18N}.detail.close`)}
                </button>
            </div>

            {isLoading && (
                <p className="text-sm text-gray-500 mt-3" aria-busy="true">
                    {t(`${I18N}.loading`)}
                </p>
            )}
            {!isLoading && error && (
                <p className="text-sm text-red-500 mt-3" role="status">
                    {t(`${I18N}.detail-error`)}
                </p>
            )}
            {!isLoading && !error && data && (
                <>
                    {hours.length > 0 && (
                        <div className="relative mt-3" style={{ height: 260 }}>
                            <Bar data={chart.data} options={chart.options} />
                        </div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                        <label className="text-xs flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={onlyShortfall}
                                onChange={(event) =>
                                    setOnlyShortfall(event.target.checked)
                                }
                            />
                            {t(`${I18N}.detail.only-shortfall`)}
                        </label>
                        <span className="text-xs text-gray-500">
                            {t(`${I18N}.local-time`)}
                        </span>
                    </div>
                    {visibleHours.length === 0 ? (
                        <p className="text-sm text-gray-500 mt-2">
                            {t(`${I18N}.detail.no-hours`)}
                        </p>
                    ) : (
                        <div
                            className="overflow-auto mt-2 bg-white rounded border border-gray-200"
                            style={{ maxHeight: 360 }}
                        >
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-gray-100 text-gray-600 uppercase">
                                    <tr>
                                        <th className="text-left p-2">
                                            {t(`${I18N}.columns.hour`)}
                                        </th>
                                        <th className="text-right p-2">
                                            {t(`${I18N}.columns.forecast`)}
                                        </th>
                                        <th className="text-right p-2">
                                            {t(`${I18N}.columns.delivered`)}
                                        </th>
                                        <th
                                            className="text-right p-2"
                                            title={t(`${I18N}.requests-hint`)}
                                        >
                                            {t(`${I18N}.columns.requests`)}
                                        </th>
                                        <th
                                            className="text-right p-2"
                                            title={t(`${I18N}.interval-hint`)}
                                        >
                                            {t(`${I18N}.columns.interval`)}
                                        </th>
                                        <th className="text-left p-2">
                                            {t(`${I18N}.columns.status`)}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleHours.map((hour) => {
                                        const style = getHourStatusStyle(
                                            hour.status
                                        );
                                        return (
                                            <tr
                                                key={hour.hour_utc}
                                                className={`border-t border-gray-100 ${
                                                    style.isProblem
                                                        ? 'bg-red-50'
                                                        : ''
                                                }`}
                                                data-testid="forecast-confidence-hour"
                                            >
                                                <td className="p-2 font-mono">
                                                    {formatHourLabel(
                                                        hour.hour_utc,
                                                        withDay
                                                    )}
                                                </td>
                                                <td className="p-2 text-right">
                                                    {formatCount(
                                                        hour.forecast_impressions
                                                    )}
                                                </td>
                                                <td className="p-2 text-right">
                                                    {formatCount(
                                                        hour.delivered_impressions
                                                    )}
                                                </td>
                                                <td className="p-2 text-right">
                                                    {formatCount(hour.requests)}
                                                </td>
                                                <td className="p-2 text-right">
                                                    {formatRequestInterval(
                                                        hour.avg_request_interval_sec,
                                                        t
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <span
                                                        className={`inline-block px-2 py-0.5 rounded ${style.className}`}
                                                    >
                                                        {t(style.labelKey)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

export default ForecastConfidenceScreenDetail;
